import { ArrowLeft20, Close20, Search16 } from '@carbon/icons-react';
import _ from 'lodash';
import { pipe } from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { View } from 'api/views/types';
import { Loading } from 'components/atoms/loading';
import { toColumnDef } from 'components/organisms/tasks/all-tasks-v2/column';
import { relativelySortColumns } from 'components/organisms/tasks/all-tasks-v2/utils';
import { constructNormalizedTask } from 'components/organisms/tasks/common';
import { default_column_order, toRows } from 'components/organisms/tasks/utils';
import { TasksViewDefinition } from 'components/organisms/tasks/views';
import { useTaskFields } from 'components/pages/all-tasks/hooks/use-task-fields';
import { useProjectDetail } from 'data/projects';
import { ProjectResource, useWithAccess } from 'hooks/rbac/useWithAccess';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useGrid } from 'hooks/use-grid';
import { useIsTablet } from 'hooks/use-media-query';
import { useTranslate } from 'hooks/use-translation';
import { useIsNativeUser } from 'hooks/use-user-type';
import { queueMacrotask } from 'utils/macrotask';
import { SearchInput } from '../../components/cp-input';
import { CpTag } from '../../components/tags/cp-tags';
import { useGetBackground, useGetStyle } from '../../types/utils';
import { CpWidgetProps } from '../../types/widget';
import { StyledBackWrapper } from '../all-files/cp-all-files';
import { BackIconWrapper } from '../chat/components';
import { CpSectionWrapper, FilterBar, PaddingSize } from '../common';
import { CpWidgetHeader } from '../common/header';
import { CpProjectTasksListLaptop } from './cp-project-tasks-list.laptop';
import { CpProjectTasksListMobile } from './cp-project-tasks-list.mobile';
import { ProjectTasksListData, TasksToBeShown } from './types';

const FilterSet = styled.div`
  width: full;
  display: flex;
  align-items: center;
  justify-content: start;

  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: var(--cp-spacing-03);
  }
`;

const CompanyFilterSet = styled(FilterSet)`
  justify-content: flex-end;

  @media (max-width: 480px) {
    flex-wrap: wrap;
    justify-content: start;
    margin-top: var(--cp-spacing-05);
    width: 100%;
  }
`;

const Tag = styled(CpTag)`
  margin-right: var(--cp-spacing-03);
`;

const StyledBackIconWrapper = styled(BackIconWrapper)`
  margin-top: 0;
`;

export const CpProjectTasksList: React.FC<CpWidgetProps> = ({
  widget,
  data: _data,
  runtimeConfig,
  onRuntimeConfigChange: _onRuntimeConfigChange,
  disabled,
  projectId,
}) => {
  const searchDebounceRef = React.useRef<NodeJS.Timeout | number | null>(null);
  const blurRef = React.useRef<VoidFunction | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isResponsive = useIsTablet();
  const { fields } = useTaskFields();
  const translate = useTranslate();
  const bg = useGetBackground(widget);
  const paddingY = useGetStyle(widget, 'paddingY', PaddingSize.Large);
  const data = useMemo(() => _data as ProjectTasksListData, [_data]);
  const { gridApi, onGridReady, gridColumnApi } = useGrid();
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );
  const [selectedViewId, setSelectedViewId] = useState<number | undefined>(
    runtimeConfig?.viewId as number | undefined,
  );
  const [search, setSearch] = useState('');

  const { data: project } = useProjectDetail(['project_id', projectId]);
  const isNativeUser = useIsNativeUser();
  const selectedView = useMemo(() => {
    if (selectedViewId === -1) {
      return {
        viewId: -1,
        viewName: translate('menu.label.tasks'),
      };
    }

    const allViews = data?.views?.concat(data?.spotlitViews ?? []);

    if (allViews) {
      return allViews.find((view) => view.viewId === selectedViewId);
    }

    return undefined;
  }, [data?.spotlitViews, data?.views, selectedViewId, translate]);

  const { hasAccessToResource } = useWithAccess();
  const projectPhaseAsReadOnlyIfNoAccess = useCallback(
    (readOnlyFields) =>
      project == null ||
      hasAccessToResource({
        rule: 'TASK_UPDATE_PHASE:WRITE',
        resourceData: ProjectResource(project),
      })
        ? readOnlyFields
        : readOnlyFields.concat(['projectPhaseId']),
    [hasAccessToResource, project],
  );
  const assigneeAsReadOnlyIfNoAccess = useCallback(
    (readOnlyFields) => readOnlyFields.concat(['assignee']),
    [],
  );
  const updatedReadonlyFields = useMemo(
    () =>
      pipe(
        assigneeAsReadOnlyIfNoAccess,
        projectPhaseAsReadOnlyIfNoAccess,
      )(default_column_order?.filter?.((field) => field !== 'jiraIntegration')),

    [assigneeAsReadOnlyIfNoAccess, projectPhaseAsReadOnlyIfNoAccess],
  );

  const rows = useMemo(() => {
    if (data?.tasks) {
      const { byId: taskLookup, ids: allMyIds } = constructNormalizedTask({
        fieldList:
          fields?.reduce?.(
            (acc, field) => ({
              ...acc,
              [field.fieldId]: field,
            }),
            {},
          ) ?? {},
        isLoading: false,
        taskList: data.tasks,
      });

      if (allMyIds[0] === 'loading') {
        return [];
      }
      const tasks = toRows(allMyIds[1], taskLookup);

      return tasks;
    }

    return [];
  }, [data.tasks, fields]);

  const onRuntimeConfigChange = useCallback(
    (newRuntimeConfig: Record<string, unknown>) => {
      if (_onRuntimeConfigChange) {
        setIsLoading(true);
        _onRuntimeConfigChange(newRuntimeConfig)?.finally(() => {
          setIsLoading(false);
        });
      }
    },
    [_onRuntimeConfigChange],
  );

  const onViewClick = useCallback(
    (viewId: number | undefined) => {
      setSelectedViewId(viewId);
      if (onRuntimeConfigChange) {
        onRuntimeConfigChange({
          ...(runtimeConfig ?? {}),
          viewId: viewId === -1 ? undefined : viewId,
        });
      }
    },
    [onRuntimeConfigChange, runtimeConfig],
  );

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    gridApi?.setColumnDefs([]);
    const toColumnDefs = pipe(
      toColumnDef({
        translate,
        isNativeUser,
        updatedReadonlyFields,
        isTimesheetFeatureEnabled,
        checkIsProjectNonBillable: () => false,
        checkIsProjectOnTaskEffortPlanning: () => true,
        hideRowSelectIcon: true,
        initialWidth: isResponsive ? 150 : 300,
      }),
      relativelySortColumns,
    );
    const columnDefs = toColumnDefs(
      fields?.filter(
        (e) =>
          !['jiraIntegration', 'projectId', 'project'].includes(e?.fieldName),
      ) ?? [],
    );

    gridApi?.setColumnDefs(columnDefs);

    if (!gridColumnApi || selectedViewId === -1 || !selectedView) {
      return;
    }

    gridColumnApi.applyColumnState({
      applyOrder: true,
      state:
        (selectedView as View<TasksViewDefinition>)?.viewDefinition
          ?.columnOrder ?? [],
    });
  }, [
    fields,
    gridApi,
    gridColumnApi,
    isNativeUser,
    isResponsive,
    isTimesheetFeatureEnabled,
    onGridReady,
    selectedView,
    selectedViewId,
    translate,
    updatedReadonlyFields,
  ]);

  useEffect(() => {
    gridApi?.setRowData(rows);
  }, [rows, gridApi]);

  useEffect(() => {
    if (data?.tasksToBeShown === TasksToBeShown.AllSharedViewsInProject) {
      const views = data?.views?.concat(data?.spotlitViews ?? []);

      setSelectedViewId(views?.[0]?.viewId);
    } else if (data?.tasksToBeShown === TasksToBeShown.OnlySpotlitSharedViews) {
      setSelectedViewId(data?.spotlitViews?.[0]?.viewId);
    } else if (data?.tasksToBeShown === TasksToBeShown.AllTasks) {
      setSelectedViewId(-1);
    }
  }, [data?.spotlitViews, data?.tasksToBeShown, data?.views]);

  const onBlur = useCallback(() => {
    if (onRuntimeConfigChange) {
      onRuntimeConfigChange({
        ...(runtimeConfig ?? {}),
        taskName: search?.trim() ? search : undefined,
      });
    }
  }, [onRuntimeConfigChange, runtimeConfig, search]);

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current as number);
      }
      const val = e.target.value;

      setSearch(val);
      queueMacrotask(() => {
        searchDebounceRef.current = setTimeout(() => {
          blurRef.current?.();
        }, 500);
      });
    },
    [],
  );

  const onClear: React.MouseEventHandler<React.ReactSVGElement> = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setSearch('');
      queueMacrotask(() => {
        blurRef.current?.();
      });
    },
    [],
  );

  const searchInput = useMemo(
    () => (
      <SearchInput
        data-cy="cp.project_tasks_list.search"
        prefix={<Search16 />}
        value={search}
        onChange={onSearchChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onBlur();
          }
        }}
        placeholder={translate('search')}
        suffix={
          isLoading ? (
            <Loading size="default" />
          ) : search ? (
            <Close20 onClick={onClear} />
          ) : undefined
        }
      />
    ),
    [isLoading, onBlur, onClear, onSearchChange, search, translate],
  );

  useEffect(() => {
    blurRef.current = onBlur;
  }, [onBlur]);

  return (
    <CpSectionWrapper background={bg} paddingY={paddingY} isDisabled={disabled}>
      {isResponsive && selectedView ? (
        <StyledBackWrapper>
          <StyledBackIconWrapper
            data-cy="cp.project_tasks_list.back"
            onClick={() => onViewClick(undefined)}
          >
            <ArrowLeft20 />
          </StyledBackIconWrapper>
          <CpWidgetHeader.Title
            autoAdjustColor
            data-cy="cp.project_tasks_list.title"
          >
            {selectedView?.viewName}
          </CpWidgetHeader.Title>
        </StyledBackWrapper>
      ) : (
        <CpWidgetHeader>
          <CpWidgetHeader.Title
            data-cy="cp.project_tasks_list.title"
            autoAdjustColor
          >
            {data?.title}
          </CpWidgetHeader.Title>
          <CpWidgetHeader.Description
            data-cy="cp.project_tasks_list.description"
            autoAdjustColor
          >
            {data?.description}
          </CpWidgetHeader.Description>
        </CpWidgetHeader>
      )}
      <FilterBar>
        <FilterSet>
          {['ALL_TASKS', 'OVERDUE', 'THIS_WEEK', 'NEXT_WEEK'].map((filter) => {
            return (
              <Tag
                data-cy={`cp.project_tasks_list.filter.${filter}`}
                autoAdjustColor
                key={filter}
                onClick={() => {
                  if (onRuntimeConfigChange) {
                    onRuntimeConfigChange({
                      ...(runtimeConfig ?? {}),
                      dateFilter: filter,
                    });
                  }
                }}
                active={
                  filter === 'ALL_TASKS'
                    ? !runtimeConfig?.dateFilter ||
                      filter === runtimeConfig?.dateFilter
                    : filter === runtimeConfig?.dateFilter
                }
              >
                <FormattedMessage
                  id={`cp.project-tasks-list.filter.${filter}`}
                />
              </Tag>
            );
          })}
        </FilterSet>
        <CompanyFilterSet>
          <Tag
            data-cy="cp.project_tasks_list.filter.company.all"
            autoAdjustColor
            active={!runtimeConfig?.responsible}
            onClick={() => {
              if (onRuntimeConfigChange) {
                onRuntimeConfigChange({
                  ...(runtimeConfig ?? {}),
                  responsible: undefined,
                });
              }
            }}
          >
            <FormattedMessage id="all" />
          </Tag>
          <Tag
            data-cy="cp.project_tasks_list.filter.company.vendor"
            autoAdjustColor
            active={runtimeConfig?.responsible === data?.vendor?.companyId}
            onClick={() => {
              if (onRuntimeConfigChange) {
                onRuntimeConfigChange({
                  ...(runtimeConfig ?? {}),
                  responsible: data?.vendor?.companyId,
                });
              }
            }}
          >
            {data?.vendor?.companyName}
          </Tag>
          {data?.partnerCompanies?.map?.((p) => (
            <Tag
              data-cy={`cp.project_tasks_list.filter.company.partner.${p?.companyName}`}
              autoAdjustColor
              active={runtimeConfig?.responsible === p?.companyId}
              onClick={() => {
                if (onRuntimeConfigChange) {
                  onRuntimeConfigChange({
                    ...(runtimeConfig ?? {}),
                    responsible: p?.companyId,
                  });
                }
              }}
            >
              {p?.companyName}
            </Tag>
          ))}
          <Tag
            data-cy="cp.project_tasks_list.filter.company.customer"
            autoAdjustColor
            active={runtimeConfig?.responsible === data?.customer?.companyId}
            onClick={() => {
              if (onRuntimeConfigChange) {
                onRuntimeConfigChange({
                  ...(runtimeConfig ?? {}),
                  responsible: data?.customer?.companyId,
                });
              }
            }}
          >
            {data?.customer?.companyName}
          </Tag>
        </CompanyFilterSet>
      </FilterBar>
      {isResponsive ? selectedViewId ? searchInput : <></> : searchInput}
      {isResponsive ? (
        <CpProjectTasksListMobile
          viewName={selectedView?.viewName ?? ''}
          gridApi={gridApi}
          onGridReady={onGridReady}
          onViewClick={onViewClick}
          rows={rows}
          sharedViews={data?.views}
          spotlitViews={data?.spotlitViews}
          tasksToBeShown={data?.tasksToBeShown}
          selectedViewId={selectedViewId}
          isLoading={isLoading}
        />
      ) : (
        <CpProjectTasksListLaptop
          viewName={selectedView?.viewName ?? ''}
          gridApi={gridApi}
          onGridReady={onGridReady}
          onViewClick={onViewClick}
          rows={rows}
          sharedViews={data?.views}
          spotlitViews={data?.spotlitViews}
          tasksToBeShown={data?.tasksToBeShown}
          selectedViewId={selectedViewId}
          isLoading={isLoading}
        />
      )}
    </CpSectionWrapper>
  );
};
